'use client';

import {
  requestCategoryLpProductCount,
  requestCategoryLpProductFilter,
} from '@apis/display/category';
import type {
  LpCategoryPath,
  LpProductCountResult,
  LpProductFilterResult,
  LpProductFilterType,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import LpFilterProductListV2 from '@components/common/LpFilterProductListV2';
import CategoryRecommendProductRow from '@components/pages/category/CategoryRecommendProductRow';
import MolocoRecProductListV2 from '@components/pages/common/MolocoRecProductListV2';
import SuspenseWrapper from '@components/common/SuspenseWrapper';
import { useCornerAreaHeart } from '@hooks/cornerArea/useCornerAreaHeart';
import { useCategoryMolocoController } from '@hooks/category/useCategoryMolocoController';
import { useCategoryProductList } from '@hooks/category/useCategoryProductList';
import useGlobalState from '@hooks/useGlobalState';
import useHistoryRouter from '@hooks/useLinkTo';
import { useSimilarBaseProduct } from '@hooks/useSimilarBaseProduct';
import type { Product } from '@wconcept/wconcept-common/models/display/product';
import type { Product as RenewalProduct } from '@wconcept/wconcept-common/models/renewal/product';
import {
  useCategoryGa,
  useCategoryModuleGa,
} from '@components/pages/category/categoryGa';
import useGa4Event from '@hooks/useGa4Event';
import type { MolocoAdProducts } from '@wconcept/wconcept-common/models/display/search';
import { addAsIsMWebDomain } from '@wconcept/wconcept-common/utils/domainUtil';
import type { CategoryPageServerProps } from '@models/categoryMain';
import ColumnToggle from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ColumnToggle';
import DesktopSkeleton from '@components/pages/common/DesktopSkeleton';
import CategoryLpSkeleton from '@wconcept/wconcept-ui-renewal/components/molecules/_skeleton/CategoryLpSkeleton';
import CategorySubSkeleton from '@wconcept/wconcept-ui-renewal/components/molecules/_skeleton/CategorySubSkeleton';
import {
  getLpProductListCols,
  getLpProductListItemUiButtonClickParams,
  setLpProductListCols,
  type LpProductListCols,
} from '@wconcept/wconcept-common/utils/lpProductListCols';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';

interface CategoryProductListProps {
  readonly id: string;
  readonly lpPath: LpCategoryPath;
  readonly shopCds?: readonly string[];
  /** 추천 Row 노출 (cateList 에서만) */
  readonly showRecommend?: boolean;
}

function CategoryProductList({
  id,
  lpPath,
  shopCds = [],
  showRecommend: showRecommendProp = false,
}: CategoryProductListProps): JSX.Element | null {
  const lastFilterTabRef = useRef<string | undefined>(undefined);
  const {
    isMobile,
    initialData,
    assistData,
    appInfo,
    addMyHeartProductItem,
    deleteMyHeartProdcutItem,
  } = useGlobalState<CategoryPageServerProps>();
  // 추천 Row 는 MO 전용 — PC 는 호출도 노출도 하지 않는다
  const showRecommend = showRecommendProp && !!isMobile;
  const [productPerView, setProductPerView] = useState<LpProductListCols>(() =>
    getLpProductListCols()
  );

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const categoryGa = useCategoryGa();
  const { sendGa4ButtonClick } = useGa4Event();
  const handleColumnToggleClick = useCallback(
    (cols: LpProductListCols) => {
      sendGa4ButtonClick({
        ...getLpProductListItemUiButtonClickParams(categoryGa.page, cols),
        page: categoryGa.page,
      });
      setProductPerView(cols);
      setLpProductListCols(cols);
    },
    [categoryGa.page, sendGa4ButtonClick]
  );

  const initialLp = initialData?.lp;
  const { linkTo } = useHistoryRouter();
  // 방금 본 상품 유사 광고 — 전부 몰로코
  const {
    sendSelectItem: sendSimilarSelectItem,
    getHeartCallbacks: getSimilarHeartCallbacks,
  } = useCategoryModuleGa('similar_ad');

  const showAds =
    (initialLp?.selectedFilterList?.length ?? 0) === 0 &&
    !initialData?.smallCategory;
  const defaultSort = initialLp?.sortInfo?.[0]?.defaultValue || '';
  const showLpAds = showAds && initialLp?.sort === defaultSort;

  const { similarBaseItemCd, rememberSimilarBase } = useSimilarBaseProduct({
    enabled: !!isMobile,
  });

  const {
    categoryMolocoAdLpProductResult,
    categoryMolocoSimilarAds,
    isSimilarFetched,
    categoryMolocoRecommendResult,
    isRecommendPending,
  } = useCategoryMolocoController({
    mediumCd: lpPath.mediumCd,
    largeCategory: lpPath.largeCategory,
    middleCategory: lpPath.middleCategory ?? '',
    smallCategory: lpPath.smallCategory,
    categoryName: initialData?.navigation?.categoryPath?.slice(-1)[0] ?? '',
    enabledLpAds: !!initialLp && showLpAds,
    mobileColumnCount: productPerView,
    similarItemCd: similarBaseItemCd,
    enabledSimilar: !!initialLp,
    recommendItems: initialData?.recommendItems,
    enabledRecommend: !!initialLp && showRecommend,
  });

  const similarMolocoList = useMemo(
    () =>
      (categoryMolocoSimilarAds ?? [])
        .filter(ad => !!ad.product)
        .map(ad => ({
          ...ad.product,
          imageUrlMobile: ad.product?.productImageUrl,
          impTrackers: ad.impTrackerList ?? [],
          clickTrackers: ad.clickTrackerList ?? [],
        })) as unknown as MolocoAdProducts[],
    [categoryMolocoSimilarAds]
  );

  // 화면에 그리는 similar 는 응답이 도착한 것만. 기준 상품이 바뀌어도 새 응답이 올 때까지 이전 row 를 유지해서
  // 삭제·생성이 한 커밋에 일어나고, 삭제 보정이 bfcache 복귀 직후 스크롤 복원(rAF)에 덮이지 않게 한다
  const [shownSimilar, setShownSimilar] = useState<{
    itemCd: string;
    list: MolocoAdProducts[];
  }>();
  useEffect(() => {
    if (similarBaseItemCd && isSimilarFetched) {
      setShownSimilar({ itemCd: similarBaseItemCd, list: similarMolocoList });
    }
  }, [similarBaseItemCd, isSimilarFetched, similarMolocoList]);

  const onClickSimilarItem = useCallback(
    (item: MolocoAdProducts, index: number) => {
      sendSimilarSelectItem(item as unknown as RenewalProduct, index, 'moloco');
      if (item.landingUrl) {
        linkTo.push({ url: item.landingUrl });
      }
    },
    [linkTo, sendSimilarSelectItem]
  );

  const similarHeartCallbacks = useMemo(
    () => getSimilarHeartCallbacks(() => 'moloco'),
    [getSimilarHeartCallbacks]
  );
  const { onClickHeart: onClickCornerHeart } = useCornerAreaHeart(
    similarHeartCallbacks
  );
  const onClickSimilarHeart = useCallback(
    (item: MolocoAdProducts, active: boolean, index: number) => {
      onClickCornerHeart(
        item as unknown as Parameters<typeof onClickCornerHeart>[0],
        active,
        index
      );
    },
    [onClickCornerHeart]
  );

  const onClickMolocoProductItem = useCallback(
    async (product: Product) => {
      linkTo.push({ url: await addAsIsMWebDomain(product.webViewUrl) });
    },
    [linkTo]
  );

  const {
    products,
    totalElements,
    totalPages,
    page,
    hasNextPage,
    isPending,
    sort,
    fetchNextPage,
    onPageChange,
    onSortChange,
    onChangePreOrderOrFreeDelivery,
    onSoldoutChange,
    onClickProduct,
    onClickHeart,
    onFilterSubmit,
    onResetFilter,
    handleResetClick,
    onClickPromoBtn,
    onFilterClose,
    onClickMolocoAdLpProduct,
  } = useCategoryProductList({
    lpPath,
    shopCds,
    initialLp,
    assistData,
    isMobile: !!isMobile,
    mobileColumnCount: productPerView,
    addMyHeartProductItem,
    deleteMyHeartProdcutItem,
  });

  const lpCommonParams = useMemo(
    () =>
      ({
        domainType: isMobile ? 'm' : 'pc',
        ...(initialLp?.genderType
          ? { gender: initialLp.genderType.toLowerCase() }
          : {}),
        ...(shopCds.length > 0 ? { shopCds } : {}),
      } as LpProductRequest),
    [isMobile, initialLp?.genderType, shopCds]
  );

  const requestProductFilter = useCallback(
    (
      type: LpProductFilterType,
      params: LpProductRequest
    ): Promise<LpProductFilterResult | undefined> =>
      requestCategoryLpProductFilter<LpProductFilterResult>(lpPath, type, {
        ...lpCommonParams,
        ...params,
      }),
    [lpPath, lpCommonParams]
  );

  const requestProductCount = useCallback(
    (params: LpProductRequest): Promise<LpProductCountResult | undefined> =>
      requestCategoryLpProductCount<LpProductCountResult>(lpPath, {
        ...lpCommonParams,
        ...params,
      }),
    [lpPath, lpCommonParams]
  );

  const sortingBarToggleItem = useMemo(() => {
    if (!isMobile) {
      return undefined;
    }
    return (
      <ColumnToggle
        perView={productPerView}
        onClick={handleColumnToggleClick}
      />
    );
  }, [isMobile, productPerView, handleColumnToggleClick]);

  const onClickProductWithSimilarBase = useCallback(
    (product: Product, index: number) => {
      if (product?.itemCd) {
        rememberSimilarBase(product.itemCd);
      }
      onClickProduct(product, index);
    },
    [rememberSimilarBase, onClickProduct]
  );

  const similarNode = useMemo(() => {
    if (!isMobile || !shownSimilar?.list.length) {
      return undefined;
    }
    return (
      <div key={shownSimilar.itemCd} style={{ width: '100%' }}>
        <MolocoRecProductListV2
          title="방금 본 상품의 추천 상품"
          bgColor
          productType="simple-small"
          list={shownSimilar.list}
          isNativeAppBoth={!!appInfo?.isNativeAppBoth}
          onClickItem={onClickSimilarItem}
          onClickHeart={onClickSimilarHeart}
          showExclusiveEmblem
        />
      </div>
    );
  }, [
    isMobile,
    shownSimilar,
    appInfo?.isNativeAppBoth,
    onClickSimilarItem,
    onClickSimilarHeart,
  ]);

  if (!initialLp) {
    return null;
  }

  const isLpPending = !isMounted || isPending;
  const isRowPending = showRecommend && (!isMounted || isRecommendPending);

  if (!isMobile && isLpPending) {
    return <DesktopSkeleton />;
  }

  let recommendNode: JSX.Element | null = null;
  if (showRecommend) {
    recommendNode = isRowPending ? (
      <CategorySubSkeleton />
    ) : (
      <div className="contents-wrap">
        <CategoryRecommendProductRow result={categoryMolocoRecommendResult} />
      </div>
    );
  }

  if (isLpPending) {
    return (
      <>
        {recommendNode}
        <CategoryLpSkeleton />
      </>
    );
  }

  return (
    <SuspenseWrapper id={id}>
      <>
        {recommendNode}
        <LpFilterProductListV2
          lastFilterTabRef={lastFilterTabRef}
          totalElements={totalElements}
          totalPages={totalPages}
          products={products}
          page={page}
          perPage={60}
          hasNextPage={hasNextPage}
          sort={sort || ''}
          tooltip
          defalutSort={initialLp.sortInfo?.[0]?.defaultValue || ''}
          sorts={initialLp.sortInfo?.[0]?.sort}
          filterBarList={initialLp.activeFilterList || []}
          originalFilters={initialLp.originalFilterList}
          selectedFilters={initialLp.selectedFilterList || []}
          fetchNextPage={fetchNextPage}
          onPageChange={onPageChange}
          onSortChange={onSortChange}
          onChangePreOrderOrFreeDelivery={onChangePreOrderOrFreeDelivery}
          onSoldoutChange={onSoldoutChange}
          onClickProduct={onClickProductWithSimilarBase}
          onClickHeart={onClickHeart}
          onFilterSubmit={onFilterSubmit}
          onResetFilter={onResetFilter}
          molocoAdLpProducts={categoryMolocoAdLpProductResult?.data}
          molocoAdLpBaseData={categoryMolocoAdLpProductResult?.baseData}
          similarNode={similarNode}
          similarAfterItemCd={shownSimilar?.itemCd}
          onClickMolocoProductItem={onClickMolocoProductItem}
          onClickMolocoAdLpProduct={onClickMolocoAdLpProduct}
          useSoldOut
          usePreOrder
          mobileVirtualColumnCount={productPerView}
          sortingBarToggleItem={sortingBarToggleItem}
          requestProductAggByType={requestProductFilter}
          requestProductCount={requestProductCount}
          onRetryFilter={() => handleResetClick('재설정')}
          onClickPromoBtn={onClickPromoBtn}
          onFilterClose={onFilterClose}
          showExclusiveEmblem
        />
      </>
    </SuspenseWrapper>
  );
}

export default React.memo(CategoryProductList);
